import React, { useState, useEffect } from 'react';
import { m } from 'kremling';
import moment from 'moment-timezone';
import { userState } from '../../../shared/user-state';
import { Button, Dropdown, DynamicTypeAhead, TimeSelector, Loader } from 'components';
import { createBroadcast, updateBroadcast, getSegments, getEmailTemplates, getSegmentCount, deleteBroadcast } from '../../../shared/common.api';
import { Modal } from '../../../components/modal/modal.component';
import { Calendar } from 'components/calendar/calendar.component';

/**
 * Get the broadcast send time.
 *
 * @param {str} sendWhen "now" or "later"
 * @param {any} broadcast form data
 * @param {moment} selectedDate date if we're sending "later"
 * @returns
 */
const getStartTime = (sendWhen, broadcast, selectedDate) => {
  let timeToUse;
  if (sendWhen === 'now') {
    timeToUse = moment().tz(broadcast.schedule_timezone).add(5, 'minutes');
  } else {
    const earliestTime = moment().tz(broadcast.schedule_timezone).add(5, 'minutes');
    const scheduledTime = selectedDate ? moment.tz(selectedDate.format('YYYY-MM-DD HH:mm:ss'), broadcast.schedule_timezone) : null;
    timeToUse = !scheduledTime || scheduledTime < earliestTime ? earliestTime : scheduledTime;
  }
  return timeToUse;
};
export const BroadcastEmailModal = props => {
  const [broadcast, setBroadcast] = useState({
    ...props.broadcast,
    drip: props.broadcast.id && props.broadcast.drip_end !== null,
    send_window: props.broadcast.id && props.broadcast.window_start !== null,
    schedule_timezone: props.broadcast && props.broadcast.schedule_timezone !== null ? moment.tz.guess() : null,
    window_timezone: props.broadcast && props.broadcast.window_timezone !== null ? moment.tz.guess() : null
  });
  const [loading, setLoading] = useState(false);
  const [estimatedSends, setEstimatedSends] = useState(null);
  const [estimatedError, setEstimatedError] = useState(false);
  const [sendWhen, setSendWhen] = useState(props.broadcast && props.broadcast.id ? 'later' : 'now');
  const [selectedDate, setSelectedDate] = useState(props.broadcast && props.broadcast.id ? moment(props.broadcast.schedule_when).tz(props.broadcast.schedule_timezone) : null);
  const [targetDate, setTargetDate] = useState(props.broadcast && props.broadcast.id && props.broadcast.drip_end ? moment(props.broadcast.drip_end).tz(props.broadcast.drip_timezone) : null);
  useEffect(() => {
    if (!broadcast.drip || !targetDate || !estimatedSends) {
      setEstimatedError(false);
      return;
    }

    // Recalculate the estimated finish time.
    let startTime = getStartTime(sendWhen, broadcast, selectedDate);
    let duration = 0;
    let targetDateTz = moment(targetDate).tz(broadcast.drip_timezone);
    if (!broadcast.send_window) {
      duration = targetDateTz - startTime;
    } else {
      // calculate duration here
      const [startHour, startMinute, startSecond] = broadcast.window_start.split(':');
      const [endHour, endMinute, endSecond] = broadcast.window_end.split(':');
      do {
        const WINDOW_START = moment(startTime).tz(broadcast.window_timezone).set({
          hour: startHour,
          minute: startMinute,
          second: startSecond,
          millisecond: 0
        });
        const WINDOW_END = moment(startTime).tz(broadcast.window_timezone).set({
          hour: endHour,
          minute: endMinute,
          second: endSecond,
          millisecond: 0
        });
        duration += Math.min(targetDateTz, WINDOW_END) - Math.max(startTime, WINDOW_START);
        startTime = startTime.add(1, 'day').set({
          hour: startHour,
          minute: startMinute,
          second: startSecond,
          millisecond: 0
        });
      } while (startTime < targetDateTz);
    }
    const numBatches = Math.ceil(estimatedSends / 100) - 1;
    setEstimatedError(duration / 1000 / numBatches < 1);
  }, [estimatedSends, sendWhen, broadcast.schedule_timezone, selectedDate, broadcast.drip, targetDate, selectedDate, broadcast.drip_timezone, broadcast.send_window, broadcast.window_start, broadcast.window_end, broadcast.window_days, broadcast.window_timezone]);
  useEffect(() => {
    // Get the broadcast send count
    if (broadcast.segment) {
      setEstimatedSends(null);
      const promise = getSegmentCount(broadcast.segment, {
        distinct: !!broadcast.distinct_emails
      });
      promise.then(({
        data
      }) => {
        setEstimatedSends(data.count);
        if (data.count >= 10000) {
          setBroadcast(broadcast => ({
            ...broadcast,
            drip: true
          }));
        }
      }).catch();
      return () => {
        promise.controller.abort();
      };
    }
  }, [broadcast.segment, broadcast.distinct_emails]);
  const updateTime = (updatedTime, setFunc) => {
    setFunc(selectedDate => {
      const [hour, minute, second] = updatedTime.split(':');
      const newDate = moment(selectedDate) || moment();
      newDate.set({
        hour,
        minute,
        second
      });
      return newDate;
    });
  };
  const saveBroadcast = () => {
    setLoading(true);
    const timeToUse = getStartTime(sendWhen, broadcast, selectedDate);
    if (!broadcast.send_window) {
      broadcast.window_start = null;
      broadcast.window_end = null;
      broadcast.window_days = null;
      broadcast.window_timezone = null;
    }
    if (!broadcast.drip) {
      broadcast.drip_end = null;
      broadcast.drip_timezone = null;
    } else {
      broadcast.drip_end = moment.tz(targetDate.format('YYYY-MM-DD HH:mm:ss'), broadcast.drip_timezone);
    }
    delete broadcast.send_window;
    delete broadcast.drip;
    let promise;
    const newBroadcast = {
      ...broadcast,
      company: userState.getAsCompanyId(),
      schedule_when: timeToUse.toDate()
    };
    if (newBroadcast.id) {
      promise = updateBroadcast(newBroadcast.id, newBroadcast);
    } else {
      promise = createBroadcast(newBroadcast);
    }
    promise.then(() => {
      props.onSave();
    }).catch(() => setLoading(false));
  };
  const canSave = () => {
    const sendNow = sendWhen === 'now' || !!selectedDate && !!broadcast.schedule_timezone;
    const dripEmails = !broadcast.drip || !!targetDate && !!broadcast.drip_timezone;
    const sendWindow = !broadcast.send_window || !!broadcast.window_start && !!broadcast.window_end && !!broadcast.window_days && broadcast.window_days.length > 0 && broadcast.window_start < broadcast.window_end;
    return !loading && !!sendNow && !!broadcast.email_template && !!broadcast.segment && dripEmails && sendWindow;
  };
  const toggleDripDay = day => {
    const window_days = broadcast.window_days || [];
    const index = window_days.indexOf(day);
    if (index === -1) {
      window_days.push(day);
    } else {
      window_days.splice(index, 1);
    }
    setBroadcast(broadcast => ({
      ...broadcast,
      window_days: [...window_days]
    }));
  };
  const deleteEmailBroadcast = broadcast => {
    deleteBroadcast(broadcast.id).then(() => {
      props.onDelete();
    });
  };
  return <Modal size="lg" open={!!broadcast} onClose={props.onClose} allowBackdropClick title={broadcast.id ? props.disabled ? 'View Email Broadcast' : 'Edit Email Broadcast' : 'Create Email Broadcast'}>
      <div className="modal__body">
        <div className="form-group">
          <label>
            Segment <span className="form-error">*</span>
          </label>
          <DynamicTypeAhead disabled={props.disabled} getItems={getSegments} getItemsFilters={{
          company: userState.getAsCompanyId()
        }} placeholder={broadcast.segment_name ? broadcast.segment_name : 'Choose Customer Segment'} displayProperty="name" keyProperty="id" value={broadcast.segment ? broadcast.segment : null} onChange={val => {
          setBroadcast(broadcast => ({
            ...broadcast,
            segment: val ? val.id : null,
            segment_name: val ? val.name : null
          }));
        }} />
        </div>
        <div className="form-group">
          <label>
            Template <span className="form-error">*</span>
          </label>
          <DynamicTypeAhead disabled={props.disabled} getItems={getEmailTemplates} getItemsFilters={{
          company: userState.getAsCompanyId(),
          archived: false,
          published: true
        }} placeholder="Choose Published Email Template" displayProperty="name" keyProperty="id" value={broadcast.email_template} onChange={val => setBroadcast(broadcast => ({
          ...broadcast,
          email_template: val ? val.id : null,
          email_template_name: val ? val.name : null
        }))} />
        </div>
        {!props.disabled && <div className="form-group space-between">
            <label>When to send broadcast?</label>
            <div className="toggler">
              <Button onClick={() => setSendWhen('now')} className={m('active', sendWhen === 'now')}>
                Now
              </Button>
              <Button onClick={() => setSendWhen('later')} className={m('active', sendWhen === 'later')}>
                Later
              </Button>
            </div>
          </div>}
        {sendWhen === 'later' && <div className="row">
            <div className="col-4">
              <div className="form-group">
                <label>Select Date</label>
                <Dropdown disabled={props.disabled} size="md" allowContentClicks trigger={() => <Button dropdown block disabled={props.disabled}>
                      {selectedDate ? selectedDate.format('LL') : 'Select Date'}
                    </Button>} content={({
              close
            }) => <div className="p-sm">
                      <Calendar value={selectedDate ? selectedDate.toDate() : null} onChange={date => {
                setSelectedDate(moment(date));
                close();
              }} />
                    </div>} />
              </div>
            </div>
            <div className="col-5">
              <div className="form-group">
                <label>Select Time</label>

                <TimeSelector disabled={props.disabled} value={selectedDate} onChange={updatedTime => {
              if (!selectedDate) setSelectedDate(moment(new Date()));
              updateTime(updatedTime, setSelectedDate);
            }} />
              </div>
            </div>

            <div className="col-5">
              <div className="form-group">
                <label>Select Timezone</label>

                <Dropdown disabled={props.disabled} size="md" contentHeight={280} trigger={() => <Button dropdown block disabled={props.disabled}>
                      {broadcast.schedule_timezone || 'Select Timezone'}
                    </Button>} content={() => <ul className="select-list">
                      {moment.tz.names().map(zone => <li key={zone}>
                          <a onClick={() => setBroadcast(broadcast => ({
                  ...broadcast,
                  schedule_timezone: zone
                }))}>
                            {zone}
                          </a>
                        </li>)}
                    </ul>} />
              </div>
            </div>
          </div>}
        <div className="form-group">
          <div className="checkbox-label">
            <label className="form-check-label">
              <input disabled={props.disabled} className="form-check-input" name="drip" type="checkbox" value={!!broadcast.drip} checked={!!broadcast.drip} htmlFor="drip" onChange={event => {
              setBroadcast(broadcast => ({
                ...broadcast,
                drip: !(event.target.value === 'true')
              }));
            }} />
              <span className="m-2">Drip emails over time</span>
            </label>
          </div>
        </div>
        {!!broadcast.drip && <div className="row">
            <div className="col-4">
              <div className="form-group">
                <label>
                  Target End Date <span className="form-error">*</span>
                </label>
                <Dropdown disabled={props.disabled} size="md" allowContentClicks trigger={() => <Button dropdown block disabled={props.disabled}>
                      {targetDate ? targetDate.format('LL') : 'Select Date'}
                    </Button>} content={({
              close
            }) => <div className="p-sm">
                      <Calendar disabled={props.disabled} value={targetDate ? targetDate.toDate() : null} onChange={date => {
                setTargetDate(moment(date));
                close();
              }} />
                    </div>} />
              </div>
            </div>
            <div className="col-5">
              <div className="form-group">
                <label>
                  Select Time <span className="form-error">*</span>
                </label>

                <TimeSelector disabled={props.disabled} value={targetDate} onChange={updatedTime => updateTime(updatedTime, setTargetDate)} />
              </div>
            </div>

            <div className="col-5">
              <div className="form-group">
                <label>
                  Select Timezone <span className="form-error">*</span>
                </label>

                <Dropdown disabled={props.disabled} size="md" contentHeight={280} trigger={() => <Button dropdown block disabled={props.disabled}>
                      {broadcast.drip_timezone || 'Select Timezone'}
                    </Button>} content={() => <ul className="select-list">
                      {moment.tz.names().map(zone => <li key={zone}>
                          <a onClick={() => setBroadcast(broadcast => ({
                  ...broadcast,
                  drip_timezone: zone
                }))}>
                            {zone}
                          </a>
                        </li>)}
                    </ul>} />
              </div>
            </div>
          </div>}
        <div className="form-group">
          <div className="checkbox-label">
            <label className="form-check-label">
              <input disabled={props.disabled} className="form-check-input" name="send_window" type="checkbox" value={!!broadcast.send_window} checked={!!broadcast.send_window} htmlFor="send_window" onChange={event => setBroadcast(broadcast => ({
              ...broadcast,
              send_window: !(event.target.value === 'true')
            }))} />
              <span className="m-2">Use send window</span>
            </label>
          </div>
        </div>
        {broadcast.send_window && <>
            <div className="row">
              <div className="form-group col-6">
                <label>
                  Start Time <span className="form-error">*</span>
                </label>

                <TimeSelector disabled={props.disabled} value={broadcast.window_start} onChange={window_start => setBroadcast(broadcast => ({
              ...broadcast,
              window_start
            }))} />
              </div>
              <div className="form-group col-6">
                <label>
                  End Time <span className="form-error">*</span>
                </label>

                <TimeSelector disabled={props.disabled} value={broadcast.window_end} onChange={window_end => setBroadcast(broadcast => ({
              ...broadcast,
              window_end
            }))} />
              </div>
            </div>
            <div className="row">
              <div className="form-group col-6">
                <label>
                  Select Timezone <span className="form-error">*</span>
                </label>

                <Dropdown disabled={props.disabled} size="md" contentHeight={280} trigger={() => <Button dropdown block disabled={props.disabled}>
                      {broadcast.window_timezone || 'Select Timezone'}
                    </Button>} content={() => <ul className="select-list">
                      {moment.tz.names().map(zone => <li key={zone}>
                          <a onClick={() => setBroadcast(broadcast => ({
                  ...broadcast,
                  window_timezone: zone
                }))}>
                            {zone}
                          </a>
                        </li>)}
                    </ul>} />
              </div>
              <div className="form-group col-6">
                <div>
                  <label>
                    Dates to Send <span className="form-error">*</span>
                  </label>
                </div>
                <div className=" d-flex space-between" style={{
              justifyContent: 'space-between'
            }}>
                  <div>
                    <label>Sun</label>
                    <br />
                    <input disabled={props.disabled} type="checkbox" value={!!broadcast.window_days && broadcast.window_days.indexOf(0) > -1} checked={!!broadcast.window_days && broadcast.window_days.indexOf(0) > -1} onChange={() => toggleDripDay(0)} />
                  </div>
                  <div>
                    <label>Mon</label>
                    <br />
                    <input disabled={props.disabled} type="checkbox" value={!!broadcast.window_days && broadcast.window_days.indexOf(1) > -1} checked={!!broadcast.window_days && broadcast.window_days.indexOf(1) > -1} onChange={() => toggleDripDay(1)} />
                  </div>
                  <div>
                    <label>Tue</label>
                    <br />
                    <input disabled={props.disabled} type="checkbox" value={!!broadcast.window_days && broadcast.window_days.indexOf(2) > -1} checked={!!broadcast.window_days && broadcast.window_days.indexOf(2) > -1} onChange={() => toggleDripDay(2)} />
                  </div>
                  <div>
                    <label>Wed</label>
                    <br />
                    <input disabled={props.disabled} type="checkbox" value={!!broadcast.window_days && broadcast.window_days.indexOf(3) > -1} checked={!!broadcast.window_days && broadcast.window_days.indexOf(3) > -1} onChange={() => toggleDripDay(3)} />
                  </div>
                  <div>
                    <label>Thu</label>
                    <br />
                    <input disabled={props.disabled} type="checkbox" value={!!broadcast.window_days && broadcast.window_days.indexOf(4) > -1} checked={!!broadcast.window_days && broadcast.window_days.indexOf(4) > -1} onChange={() => toggleDripDay(4)} />
                  </div>
                  <div>
                    <label>Fri</label>
                    <br />
                    <input disabled={props.disabled} type="checkbox" value={!!broadcast.window_days && broadcast.window_days.indexOf(5) > -1} checked={!!broadcast.window_days && broadcast.window_days.indexOf(5) > -1} onChange={() => toggleDripDay(5)} />
                  </div>
                  <div>
                    <label>Sat</label>
                    <br />
                    <input disabled={props.disabled} type="checkbox" value={!!broadcast.window_days && broadcast.window_days.indexOf(6) > -1} checked={!!broadcast.window_days && broadcast.window_days.indexOf(6) > -1} onChange={() => toggleDripDay(6)} />
                  </div>
                </div>
              </div>
            </div>
          </>}
        <div className="form-group">
          <div className="checkbox-label">
            <label className="form-check-label">
              <input disabled={props.disabled} className="form-check-input" name="distinct_emails" type="checkbox" htmlFor="distinct_emails" value={!!broadcast.distinct_emails} checked={!!broadcast.distinct_emails} onChange={event => {
              setBroadcast(broadcast => ({
                ...broadcast,
                distinct_emails: !(event.target.value === 'true')
              }));
            }} />
              <span className="m-2">Send only one email per email address</span>
            </label>
          </div>
        </div>
        <div className="form-group">
          <label>Google Analytics Campaign Title</label>
          <input disabled={props.disabled} className="form-control" name="google_campaign" onChange={e => {
          const value = e.target.value.split(' ').join('_');
          setBroadcast(broadcast => ({
            ...broadcast,
            google_campaign: value
          }));
        }} placeholder="Optional" value={broadcast.google_campaign || ''} type="text" />
        </div>
        {broadcast.drip && broadcast.segment && estimatedSends === null && <p>
            <em>Loading estimates...</em>
          </p>}
        {!!estimatedError && <p className="text-danger">
            Warning. Due to current settings the broadcast won't finish before
            the target end date and will continue to send afterward.
          </p>}
        <div className="form-error text-sm">* Required</div>
      </div>
      <div className="modal__actions">
        <div className='d-flex justify-content-between'>
          {broadcast.id && !props.disabled && <Button actionType="danger" onClick={() => {
          deleteEmailBroadcast(broadcast);
        }}>
              Delete
            </Button> || <div> </div>}
          <div>
            {!props.disabled && <Button onClick={() => props.onClose()}>Cancel</Button>}

            <Button actionType="primary" onClick={() => !props.disabled ? saveBroadcast() : props.onClose()} className="float-right" disabled={!canSave()}>
              {loading ? <Loader size="sm" /> : broadcast.id ? props.disabled ? 'Close' : 'Edit' : 'Create'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>;
};